import { twMerge } from "tailwind-merge";
import {
  OnboardingStepperBase,
  type OnboardingStepperBaseProps,
} from "@core/ui";
import type { StepState } from "@core/features/onboarding";
import { Button } from "@themes/pollinator/ui/Button";
import { OnboardingStepperItem } from "../StepperItem";

export type Props = Omit<
  OnboardingStepperBaseProps,
  "renderStep" | "renderActions"
>;

/**
 * Styled stepper component for the onboarding flow.
 * Displays a linear list of steps with completion status.
 *
 * @example
 * ```tsx
 * <OnboardingStepper
 *   stepStates={stepStates}
 *   canSkip={canSkip}
 *   isLastStep={isLastStep}
 *   onStepClick={handleStepClick}
 *   onSkipClick={handleSkipClick}
 *   onContinueClick={handleContinueClick}
 * />
 * ```
 */
export function OnboardingStepper({
  stepStates,
  canSkip,
  isLastStep,
  onStepClick,
  onSkipClick,
  onContinueClick,
  className,
  ...props
}: Props) {
  const renderStep = (stepState: StepState) => (
    <OnboardingStepperItem
      {...stepState}
      onClick={() => stepState.isClickable && onStepClick?.(stepState.index)}
    />
  );

  const renderActions = ({
    canSkip,
    isLastStep,
    onSkipClick,
    onContinueClick,
  }: {
    canSkip: boolean;
    isLastStep: boolean;
    onSkipClick?: () => void;
    onContinueClick?: () => void;
  }) => (
    <div className="flex gap-3 mt-4">
      {onContinueClick && (
        <Button
          onClick={onContinueClick}
          variant="primary"
          className="flex-1"
          theme="dark"
        >
          {isLastStep ? "Complete" : "Continue"}
        </Button>
      )}
      {canSkip && onSkipClick && (
        <Button onClick={onSkipClick} variant="ghost" theme="dark">
          Skip for now
        </Button>
      )}
    </div>
  );

  return (
    <OnboardingStepperBase
      stepStates={stepStates}
      canSkip={canSkip}
      isLastStep={isLastStep}
      onStepClick={onStepClick}
      onSkipClick={onSkipClick}
      onContinueClick={onContinueClick}
      renderStep={renderStep}
      renderActions={renderActions}
      className={twMerge("flex flex-col gap-6", className)}
      stepsClassName="flex flex-col gap-2"
      {...props}
    />
  );
}

export default OnboardingStepper;
